import { useState, useEffect, useRef, useCallback } from 'react';
import { langName } from '../lib/lang-names';
import { parseCefrToBook, BOOK_LEVELS, LEVEL_NAMES, LEVEL_PRICES, type ParsedBook } from '../lib/book-parser';
import { renderBookPreviewHtml } from '../lib/book-renderer';
import { buildEpub, downloadBlob } from '../lib/epub-builder';
import { generateBookCopywriting, type BookCopywriting } from '../lib/book-copywriter';
import ImageStudio from '../components/ImageStudio';
import ImagePicker, { type ImagePickerSlot } from '../components/ImagePicker';

// ── Types ──────────────────────────────────────────────────────────

interface CefrDocEntry {
  key: string;
  language_code: string;
  level: string;
  base_lang?: string;
}

const BASE_LANG_NAMES: Record<string, string> = {
  en: 'English', ru: 'Russian', es: 'Spanish', fr: 'French', de: 'German',
  pt: 'Portuguese', it: 'Italian', ja: 'Japanese', ko: 'Korean',
  'zh-TW': 'Chinese (Traditional)', 'zh-CN': 'Chinese (Simplified)',
  th: 'Thai', ar: 'Arabic', vi: 'Vietnamese', tr: 'Turkish',
  pl: 'Polish', nl: 'Dutch', sv: 'Swedish', uk: 'Ukrainian',
  id: 'Indonesian', hi: 'Hindi', he: 'Hebrew', el: 'Greek',
};

interface BookRecord {
  language_code: string;
  level: string;
  status: 'generating' | 'ready' | 'error';
  epub_url?: string;
  pdf_url?: string;
  cover_url?: string;
  chapter_count: number;
  generated_at: string;
}

interface PreflightCheck {
  id: string;
  label: string;
  pass: boolean;
  detail: string;
}

interface PreflightResult {
  checks: PreflightCheck[];
  allPassed: boolean;
  docContent?: string;
}

// ── Constants ──────────────────────────────────────────────────────

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? 'https://ojbr4cjpvh.execute-api.ap-northeast-1.amazonaws.com';

const LEVEL_COLORS: Record<string, { bg: string; text: string; badge: string }> = {
  A0: { bg: 'bg-purple-900/30', text: 'text-purple-300', badge: 'bg-purple-900 text-purple-300' },
  A1: { bg: 'bg-emerald-900/30', text: 'text-emerald-300', badge: 'bg-emerald-900 text-emerald-300' },
  A2: { bg: 'bg-teal-900/30', text: 'text-teal-300', badge: 'bg-teal-900 text-teal-300' },
  B1: { bg: 'bg-blue-900/30', text: 'text-blue-300', badge: 'bg-blue-900 text-blue-300' },
  B2: { bg: 'bg-indigo-900/30', text: 'text-indigo-300', badge: 'bg-indigo-900 text-indigo-300' },
  C1: { bg: 'bg-amber-900/30', text: 'text-amber-300', badge: 'bg-amber-900 text-amber-300' },
  C2: { bg: 'bg-rose-900/30', text: 'text-rose-300', badge: 'bg-rose-900 text-rose-300' },
};

const DEFAULT_COLOR = { bg: 'bg-zinc-900/30', text: 'text-zinc-300', badge: 'bg-zinc-900 text-zinc-300' };

type Tab = 'browse' | 'preflight' | 'generate' | 'pipeline' | 'covers';

// ── Helpers ────────────────────────────────────────────────────────

async function fetchCefrDoc(key: string): Promise<string | null> {
  try {
    const res = await fetch(`${BASE_URL}/api/admin/docs/${encodeURIComponent(key)}`);
    if (res.ok) {
      const data = await res.json() as { content?: string };
      if (data.content) return data.content;
    }
  } catch { /* fall through */ }
  return null;
}

async function fetchDocList(): Promise<CefrDocEntry[]> {
  try {
    const res = await fetch(`${BASE_URL}/api/admin/docs`);
    if (res.ok) {
      const data = await res.json() as { docs: CefrDocEntry[] };
      return data.docs;
    }
  } catch { /* fall through */ }
  return [];
}

function levelBadge(level: string) {
  const c = LEVEL_COLORS[level] ?? DEFAULT_COLOR;
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.badge}`}>
      {level} {LEVEL_NAMES[level] ?? ''}
    </span>
  );
}

function statusBadge(status: string) {
  const colors: Record<string, string> = {
    ready: 'bg-emerald-900 text-emerald-300',
    generating: 'bg-amber-900 text-amber-300',
    error: 'bg-red-900 text-red-300',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] ?? 'bg-zinc-700 text-zinc-300'}`}>
      {status}
    </span>
  );
}

// ── Browse Tab ─────────────────────────────────────────────────────

function BrowseBooks() {
  const [books, setBooks] = useState<BookRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState<BookRecord | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetch(`${BASE_URL}/api/admin/books`)
      .then(r => r.ok ? r.json() as Promise<{ books: BookRecord[] }> : { books: [] })
      .then(d => setBooks(d.books))
      .catch(() => setBooks([]))
      .finally(() => setLoading(false));
  }, []);

  const loadPreview = useCallback(async (book: BookRecord) => {
    setSelectedBook(book);
    setLoadingPreview(true);
    setPreviewHtml(null);

    const key = `${book.level}_${book.language_code}`;
    const md = await fetchCefrDoc(key);
    if (!md) {
      setPreviewHtml('<p style="color:#f87171;padding:2rem;">Failed to load document content.</p>');
      setLoadingPreview(false);
      return;
    }

    const parsed = parseCefrToBook(md, book.language_code, langName(book.language_code), book.level);
    const html = renderBookPreviewHtml(parsed, { coverImageUrl: book.cover_url });
    setPreviewHtml(html);
    setLoadingPreview(false);
  }, []);

  if (loading) return <p className="text-zinc-500">Loading books...</p>;

  if (books.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-zinc-600 text-4xl mb-4">📚</div>
        <h3 className="text-zinc-300 text-lg font-medium mb-2">No books generated yet</h3>
        <p className="text-zinc-500 text-sm">Use the Generate tab to create your first book.</p>
      </div>
    );
  }

  // Group by language
  const byLang = new Map<string, BookRecord[]>();
  for (const b of books) {
    const list = byLang.get(b.language_code) ?? [];
    list.push(b);
    byLang.set(b.language_code, list);
  }

  if (selectedBook && previewHtml) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => { setSelectedBook(null); setPreviewHtml(null); }}
          className="text-teal-400 hover:text-teal-300 text-sm"
        >
          &larr; Back to list
        </button>
        <div className="flex flex-col sm:flex-row gap-3 items-start">
          <h3 className="text-lg font-medium text-zinc-200">
            {langName(selectedBook.language_code)} — {LEVEL_NAMES[selectedBook.level]}
          </h3>
          {levelBadge(selectedBook.level)}
          {statusBadge(selectedBook.status)}
        </div>
        {loadingPreview ? (
          <p className="text-zinc-500">Loading preview...</p>
        ) : (
          <div className="rounded-lg overflow-hidden border border-zinc-700">
            <iframe
              srcDoc={previewHtml}
              title="Book preview"
              className="w-full bg-white"
              style={{ height: '80vh' }}
              sandbox="allow-same-origin"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {[...byLang.entries()].sort((a, b) => langName(a[0]).localeCompare(langName(b[0]))).map(([lang, langBooks]) => (
        <div key={lang} className="bg-zinc-800/50 rounded-lg p-4">
          <h3 className="text-zinc-200 font-medium mb-3">{langName(lang)}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {BOOK_LEVELS.map(lvl => {
              const book = langBooks.find(b => b.level === lvl);
              const c = LEVEL_COLORS[lvl] ?? DEFAULT_COLOR;
              if (!book) {
                return (
                  <div key={lvl} className="bg-zinc-900/50 rounded p-3 text-center opacity-40">
                    <div className={`text-xs font-medium ${c.text}`}>{lvl}</div>
                    <div className="text-zinc-600 text-xs mt-1">—</div>
                  </div>
                );
              }
              return (
                <button
                  key={lvl}
                  onClick={() => loadPreview(book)}
                  className={`${c.bg} rounded p-3 text-center hover:ring-1 hover:ring-teal-500 transition-all cursor-pointer`}
                >
                  <div className={`text-xs font-medium ${c.text}`}>{lvl}</div>
                  <div className="text-zinc-300 text-xs mt-1">{LEVEL_NAMES[lvl]}</div>
                  <div className="mt-1">{statusBadge(book.status)}</div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Preflight Tab ──────────────────────────────────────────────────

function Preflight() {
  const [docs, setDocs] = useState<CefrDocEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLang, setSelectedLang] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('A1');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<PreflightResult | null>(null);

  useEffect(() => {
    fetchDocList().then(d => { setDocs(d); setLoading(false); });
  }, []);

  const availableLangs = new Set<string>();
  for (const d of docs) availableLangs.add(d.language_code);
  const langList = [...availableLangs].sort((a, b) => langName(a).localeCompare(langName(b)));

  const hasDoc = (lvl: string, lang: string): boolean => {
    const key = `${lvl}_${lang}`;
    return docs.some(d => d.key === key);
  };

  const runPreflight = async () => {
    if (!selectedLang) return;
    setChecking(true);
    setResult(null);

    const checks: PreflightCheck[] = [];
    const key = `${selectedLevel}_${selectedLang}`;

    // Check 1: Document exists
    const md = await fetchCefrDoc(key);
    checks.push({
      id: 'doc-exists',
      label: `${selectedLevel} document exists for ${langName(selectedLang)}`,
      pass: !!md,
      detail: md ? `Document loaded (${md.length.toLocaleString()} chars)` : `No ${key} document found in database.`,
    });

    if (!md) {
      setResult({ checks, allPassed: false });
      setChecking(false);
      return;
    }

    // Check 2: Minimum length
    const lineCount = md.split('\n').length;
    const minLines = selectedLevel === 'A0' ? 50 : selectedLevel === 'A1' ? 300 : 80;
    checks.push({
      id: 'min-length',
      label: `Minimum content length (${minLines}+ lines)`,
      pass: lineCount >= minLines,
      detail: `Document has ${lineCount} lines (minimum: ${minLines}).`,
    });

    // Check 3: Has sections (for A1+)
    if (selectedLevel !== 'A0') {
      const sectionMatches = md.match(/^## /gm) ?? [];
      checks.push({
        id: 'has-sections',
        label: 'Has structured sections (## headers)',
        pass: sectionMatches.length >= 3,
        detail: `Found ${sectionMatches.length} top-level sections.`,
      });

      // Check 4: Has vocabulary (§9)
      const hasVocab = /^## (?:§\s*)?9[\s.:,—-]/m.test(md) || /^### (?:§\s*)?9\.\d/m.test(md);
      checks.push({
        id: 'has-vocab',
        label: 'Has vocabulary section (§9)',
        pass: hasVocab,
        detail: hasVocab ? 'Vocabulary section found.' : 'No §9 vocabulary section detected.',
      });

      // Check 5: Has grammar notes (§10)
      const hasGrammar = /^## (?:§\s*)?10[\s.:,—-]/m.test(md) || /^### (?:§\s*)?10\.\d/m.test(md);
      checks.push({
        id: 'has-grammar',
        label: 'Has grammar notes (§10)',
        pass: hasGrammar,
        detail: hasGrammar ? 'Grammar notes section found.' : 'No §10 grammar section detected.',
      });
    }

    // Check 6: Has tables (vocab/grammar tables expected)
    const tableCount = (md.match(/^\|.+\|$/gm) ?? []).length;
    checks.push({
      id: 'has-tables',
      label: 'Contains data tables',
      pass: tableCount >= 5,
      detail: `Found ${tableCount} table rows.`,
    });

    // Check 7: No Unicode corruption
    const hasBadChars = /�/.test(md);
    checks.push({
      id: 'no-corruption',
      label: 'No Unicode corruption (U+FFFD)',
      pass: !hasBadChars,
      detail: hasBadChars ? 'WARNING: Found replacement characters (U+FFFD) — content may be corrupted.' : 'No corruption detected.',
    });

    // Check 8: Parse into chapters
    const parsed = parseCefrToBook(md, selectedLang, langName(selectedLang), selectedLevel);
    checks.push({
      id: 'chapter-count',
      label: 'Parseable into chapters',
      pass: parsed.chapters.length >= 1,
      detail: `Parsed ${parsed.chapters.length} chapters with ${parsed.chapters.reduce((s, c) => s + c.subsections.length, 0)} subsections.`,
    });

    const allPassed = checks.every(c => c.pass);
    setResult({ checks, allPassed, docContent: md });
    setChecking(false);
  };

  // Level availability matrix
  const renderAvailabilityMatrix = () => {
    if (!selectedLang) return null;
    return (
      <div className="bg-zinc-800/50 rounded-lg p-4 mt-4">
        <h4 className="text-zinc-300 text-sm font-medium mb-3">
          Document availability for {langName(selectedLang)}
        </h4>
        <div className="flex flex-wrap gap-2">
          {BOOK_LEVELS.map(lvl => {
            const available = hasDoc(lvl, selectedLang);
            const c = LEVEL_COLORS[lvl] ?? DEFAULT_COLOR;
            return (
              <button
                key={lvl}
                onClick={() => setSelectedLevel(lvl)}
                className={`px-3 py-2 rounded text-xs font-medium transition-all ${
                  selectedLevel === lvl
                    ? `${c.bg} ${c.text} ring-1 ring-teal-500`
                    : available
                      ? `${c.bg} ${c.text} hover:ring-1 hover:ring-zinc-600`
                      : 'bg-zinc-900/50 text-zinc-600 cursor-not-allowed'
                }`}
                disabled={!available}
              >
                {lvl} {LEVEL_NAMES[lvl]}
                <span className="block mt-0.5 text-[10px]">
                  {available ? '✓ available' : '— missing'}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-zinc-800 rounded-lg p-4 md:p-6">
        <h3 className="text-zinc-200 font-medium mb-4">Pre-flight Check</h3>
        <p className="text-zinc-400 text-sm mb-4">
          Validates that the CEFR document exists and has sufficient content for book generation.
        </p>

        <div className="flex flex-wrap gap-3">
          <div className="w-full sm:w-64">
            <label className="text-zinc-500 text-xs block mb-1">Language</label>
            <select
              value={selectedLang}
              onChange={e => { setSelectedLang(e.target.value); setResult(null); }}
              className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200"
            >
              <option value="">Select language…</option>
              {langList.map(l => (
                <option key={l} value={l}>{langName(l)} ({l})</option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-48">
            <label className="text-zinc-500 text-xs block mb-1">Level</label>
            <select
              value={selectedLevel}
              onChange={e => { setSelectedLevel(e.target.value); setResult(null); }}
              className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200"
            >
              {BOOK_LEVELS.map(lvl => (
                <option key={lvl} value={lvl}>{lvl} — {LEVEL_NAMES[lvl]} (${LEVEL_PRICES[lvl]})</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={runPreflight}
              disabled={!selectedLang || checking}
              className="px-4 py-2 rounded bg-teal-800 text-teal-200 text-sm font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >
              {checking ? 'Checking…' : 'Run Pre-flight'}
            </button>
          </div>
        </div>

        {renderAvailabilityMatrix()}
      </div>

      {result && (
        <div className="bg-zinc-800 rounded-lg p-4 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className={`text-lg ${result.allPassed ? 'text-emerald-400' : 'text-amber-400'}`}>
              {result.allPassed ? '✓' : '⚠'}
            </span>
            <h3 className="text-zinc-200 font-medium">
              {result.allPassed ? 'All checks passed — ready to generate' : 'Some checks failed'}
            </h3>
          </div>
          <div className="space-y-2">
            {result.checks.map(check => (
              <div
                key={check.id}
                className={`flex items-start gap-3 p-3 rounded ${
                  check.pass ? 'bg-emerald-900/20' : 'bg-red-900/20'
                }`}
              >
                <span className={`text-sm mt-0.5 ${check.pass ? 'text-emerald-400' : 'text-red-400'}`}>
                  {check.pass ? '✓' : '✗'}
                </span>
                <div>
                  <div className={`text-sm font-medium ${check.pass ? 'text-emerald-300' : 'text-red-300'}`}>
                    {check.label}
                  </div>
                  <div className="text-xs text-zinc-400 mt-0.5">{check.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Generate Tab ───────────────────────────────────────────────────

interface GenLog {
  timestamp: string;
  message: string;
  level: 'info' | 'warn' | 'error';
}

function GenerateBooks() {
  const [docs, setDocs] = useState<CefrDocEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLang, setSelectedLang] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('A1');
  const [selectedBase, setSelectedBase] = useState('en');
  const [generating, setGenerating] = useState(false);
  const [logs, setLogs] = useState<GenLog[]>([]);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [parsedBook, setParsedBook] = useState<ParsedBook | null>(null);
  const [copywriting, setCopywriting] = useState<BookCopywriting | null>(null);
  const [epubReady, setEpubReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [coverUrls, setCoverUrls] = useState<{ male?: string; female?: string } | null>(null);
  const [coverApproval, setCoverApproval] = useState<{ male: 'pending' | 'approved' | 'rejected'; female: 'pending' | 'approved' | 'rejected' }>({ male: 'pending', female: 'pending' });
  const [generatingCover, setGeneratingCover] = useState(false);
  const [regeneratingCover, setRegeneratingCover] = useState<string | null>(null);
  const [illustrations, setIllustrations] = useState<Array<{ chapter: number; url: string | null; approved: boolean }>>([]);
  const [generatingIllustrations, setGeneratingIllustrations] = useState(false);
  const [regeneratingChapter, setRegeneratingChapter] = useState<number | null>(null);
  const [recovering, setRecovering] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSlot, setPickerSlot] = useState<ImagePickerSlot>('cover_male');
  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchDocList().then(d => { setDocs(d); setLoading(false); });
  }, []);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // Keep the preview in sync with the images: re-render whenever a cover or chapter
  // illustration is generated / uploaded / approved. Previously the preview was built once
  // at generate time, so newly created images never appeared in it.
  useEffect(() => {
    if (!parsedBook) return;
    const cover = coverUrls?.female || coverUrls?.male || undefined;
    const chapterImageUrls: Record<string, string> = {};
    for (const ill of illustrations) if (ill.url) chapterImageUrls[`ch-${ill.chapter}`] = ill.url;
    setPreviewHtml(renderBookPreviewHtml(parsedBook, {
      coverImageUrl: cover,
      chapterImageUrls: Object.keys(chapterImageUrls).length ? chapterImageUrls : undefined,
    }));
  }, [parsedBook, coverUrls, coverApproval, illustrations]);

  const availableBases = new Set<string>();
  for (const d of docs) availableBases.add(d.base_lang || 'en');
  const baseList = [...availableBases].sort((a, b) => (BASE_LANG_NAMES[a] ?? a).localeCompare(BASE_LANG_NAMES[b] ?? b));

  const availableLangs = new Set<string>();
  for (const d of docs) {
    if ((d.base_lang || 'en') === selectedBase) availableLangs.add(d.language_code);
  }
  const langList = [...availableLangs].sort((a, b) => langName(a).localeCompare(langName(b)));

  const addLog = (msg: string, level: GenLog['level'] = 'info') => {
    setLogs(prev => [...prev, { timestamp: new Date().toISOString(), message: msg, level }]);
  };

  const generate = async () => {
    if (!selectedLang) return;
    setGenerating(true);
    setLogs([]);
    setPreviewHtml(null);
    setParsedBook(null);
    setEpubReady(false);

    const langFullName = langName(selectedLang);
    addLog(`Starting book generation: ${langFullName} ${selectedLevel} (${LEVEL_NAMES[selectedLevel]})`);

    // Step 1: Fetch CEFR document
    const key = selectedBase === 'en' ? `${selectedLevel}_${selectedLang}` : `${selectedLevel}_${selectedLang}__${selectedBase}`;
    addLog(`Fetching CEFR document: ${key} (base: ${BASE_LANG_NAMES[selectedBase] ?? selectedBase})…`);
    const md = await fetchCefrDoc(key);

    if (!md) {
      addLog(`ERROR: No ${key} document found. Run pre-flight to check availability.`, 'error');
      setGenerating(false);
      return;
    }
    addLog(`Document loaded: ${md.length.toLocaleString()} chars, ${md.split('\n').length} lines`);

    // Step 2: Parse into chapters
    addLog('Parsing document into chapters…');
    const parsed = parseCefrToBook(md, selectedLang, langFullName, selectedLevel);
    setParsedBook(parsed);
    addLog(`Parsed ${parsed.chapters.length} chapters with ${parsed.chapters.reduce((s, c) => s + c.subsections.length, 0)} subsections`);

    if (parsed.preamble.trim()) {
      addLog('Found language overview preamble — will include as Introduction chapter');
    }

    for (const ch of parsed.chapters) {
      addLog(`  Chapter ${ch.number}: ${ch.title} (${ch.subsections.length} subsections)`);
    }

    // Step 2b: Load existing book images from DB
    const bookKey = `${selectedLang}_${selectedLevel}`;
    let savedCoverUrl: string | undefined;
    let savedChapterImages: Record<string, string> = {};
    try {
      const bookRes = await fetch(`${BASE_URL}/api/admin/books/${encodeURIComponent(bookKey)}`);
      if (bookRes.ok) {
        const existing = await bookRes.json() as {
          cover_url?: string; cover_male_url?: string; cover_female_url?: string;
          illustrations?: Array<{ chapter: number; url: string }>;
        };
        const hasCover = existing.cover_male_url || existing.cover_female_url;
        if (hasCover) {
          setCoverUrls({ male: existing.cover_male_url, female: existing.cover_female_url });
          setCoverApproval({
            male: existing.cover_male_url ? 'approved' : 'pending',
            female: existing.cover_female_url ? 'approved' : 'pending',
          });
          savedCoverUrl = existing.cover_url || existing.cover_female_url || existing.cover_male_url;
          addLog(`Loaded saved cover images (male: ${existing.cover_male_url ? 'yes' : 'no'}, female: ${existing.cover_female_url ? 'yes' : 'no'})`);
        }
        if (existing.illustrations?.length) {
          setIllustrations(existing.illustrations.map(i => ({ chapter: i.chapter, url: i.url, approved: true })));
          for (const ill of existing.illustrations) {
            savedChapterImages[`ch-${ill.chapter}`] = ill.url;
          }
          addLog(`Loaded ${existing.illustrations.length} saved chapter illustrations`);
        }
      }
    } catch { /* no existing book, start fresh */ }

    // Step 3: Generate copywriting
    addLog('Generating landing page copywriting…');
    const copy = generateBookCopywriting(parsed);
    setCopywriting(copy);
    addLog(`Copywriting ready: "${copy.blurb.slice(0, 80)}…"`);
    addLog(`Stats: ${copy.stats.word_count.toLocaleString()} words, ${copy.stats.vocab_items} vocab items, ${copy.stats.grammar_topics} grammar topics`);

    // Step 4: Render book HTML preview
    addLog('Rendering book HTML…');
    const html = renderBookPreviewHtml(parsed, {
      coverImageUrl: savedCoverUrl,
      chapterImageUrls: Object.keys(savedChapterImages).length > 0 ? savedChapterImages : undefined,
    });
    setPreviewHtml(html);
    addLog('Book preview rendered');

    // Step 5: Build EPUB
    addLog('Building EPUB package…');
    try {
      await buildEpub(parsed);
      setEpubReady(true);
      addLog('EPUB package ready for download');
    } catch (err) {
      addLog(`EPUB build error: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }

    addLog(`Book generation complete: Glossika ${langFullName} — ${LEVEL_NAMES[selectedLevel]}`);
    setGenerating(false);
  };

  const downloadEpub = async () => {
    if (!parsedBook) return;
    addLog('Building EPUB for download…');
    try {
      const blob = await buildEpub(parsedBook);
      const filename = `Glossika_${parsedBook.metadata.languageName.replace(/[^a-zA-Z0-9]/g, '_')}_${parsedBook.metadata.level}_${LEVEL_NAMES[parsedBook.metadata.level]?.replace(/\s+/g, '_')}.epub`;
      downloadBlob(blob, filename);
      addLog(`Downloaded: ${filename}`);
    } catch (err) {
      addLog(`Download error: ${err instanceof Error ? err.message : String(err)}`, 'error');
    }
  };

  const downloadPdf = () => {
    if (!previewHtml) return;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(previewHtml.replace('book-preview-frame', ''));
      printWindow.document.close();
      addLog('Opened print window — use browser Print → Save as PDF');
    }
  };

  const hasUnapprovedImages = () => {
    if (coverUrls) {
      if (coverUrls.male && coverApproval.male !== 'approved') return true;
      if (coverUrls.female && coverApproval.female !== 'approved') return true;
    }
    if (illustrations.some(i => i.url && !i.approved)) return true;
    return false;
  };

  const saveToDatabase = async () => {
    if (!parsedBook || !copywriting) return;
    if (hasUnapprovedImages()) {
      addLog('Cannot save: approve all IKON images first (or regenerate rejected ones)', 'warn');
      return;
    }
    setSaving(true);
    const bookKey = `${parsedBook.metadata.languageCode}_${parsedBook.metadata.level}`;
    addLog(`Saving book record: ${bookKey}…`);

    try {
      const approvedIllustrations = illustrations
        .filter(i => i.url && i.approved)
        .map(i => ({ chapter: i.chapter, url: i.url }));

      const body: Record<string, unknown> = {
        language_code: parsedBook.metadata.languageCode,
        level: parsedBook.metadata.level,
        language_name: parsedBook.metadata.languageName,
        title: copywriting.title,
        subtitle: copywriting.subtitle,
        status: 'ready',
        chapter_count: parsedBook.chapters.length,
        subsection_count: parsedBook.chapters.reduce((s, c) => s + c.subsections.length, 0),
        generated_at: new Date().toISOString(),
        copywriting,
      };
      if (coverUrls?.male && coverApproval.male === 'approved') body.cover_male_url = coverUrls.male;
      if (coverUrls?.female && coverApproval.female === 'approved') body.cover_female_url = coverUrls.female;
      if (coverUrls) body.cover_url = coverUrls.female || coverUrls.male || null;
      if (approvedIllustrations.length > 0) body.illustrations = approvedIllustrations;

      const res = await fetch(`${BASE_URL}/api/admin/books/${encodeURIComponent(bookKey)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Metadata save failed: HTTP ${res.status}`);
      addLog(`Metadata saved: ${bookKey}`);

      // Upload master EPUB to S3 so the deliver Lambda can serve it
      addLog('Building and uploading master EPUB to S3…');
      const epubBlob = await buildEpub(parsedBook);
      const arrayBuffer = await epubBlob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
      const base64 = btoa(binary);

      const uploadRes = await fetch(`${BASE_URL}/api/admin/books/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          language_code: parsedBook.metadata.languageCode,
          level: parsedBook.metadata.level,
          format: 'epub',
          content_base64: base64,
        }),
      });
      if (!uploadRes.ok) throw new Error(`EPUB upload failed: HTTP ${uploadRes.status}`);
      addLog('Master EPUB uploaded to S3 — ready for consumer delivery');
    } catch (err) {
      addLog(`Save error: ${err instanceof Error ? err.message : String(err)}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const generateCover = async (gender?: string) => {
    if (!parsedBook) return;
    const isRegen = !!gender;
    if (isRegen) setRegeneratingCover(gender!); else setGeneratingCover(true);
    addLog(isRegen ? `Regenerating IKON cover (${gender})…` : 'Requesting IKON cover images (male + female)…');

    try {
      const res = await fetch(`${BASE_URL}/api/admin/books/cover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language_code: parsedBook.metadata.languageCode, level: parsedBook.metadata.level }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { cover: { male?: { url: string }; female?: { url: string } } };

      if (isRegen) {
        const newUrl = gender === 'male' ? data.cover?.male?.url : data.cover?.female?.url;
        if (newUrl) {
          setCoverUrls(prev => prev ? { ...prev, [gender!]: newUrl } : { [gender!]: newUrl });
          setCoverApproval(prev => ({ ...prev, [gender!]: 'pending' as const }));
          addLog(`Regenerated cover (${gender}): ${newUrl.slice(0, 80)}…`);
        }
      } else {
        const urls = {
          male: data.cover?.male?.url || undefined,
          female: data.cover?.female?.url || undefined,
        };
        setCoverUrls(urls);
        setCoverApproval({ male: 'pending', female: 'pending' });
        if (urls.male) addLog(`Cover (male): ${urls.male.slice(0, 80)}…`);
        if (urls.female) addLog(`Cover (female): ${urls.female.slice(0, 80)}…`);
        if (!urls.male && !urls.female) addLog('IKON returned no cover images — IKON may be unavailable', 'warn');
      }
    } catch (err) {
      addLog(`Cover generation error: ${err instanceof Error ? err.message : String(err)}`, 'error');
    } finally {
      setGeneratingCover(false);
      setRegeneratingCover(null);
    }
  };

  // Generate IKON illustrations ONE CHAPTER PER REQUEST (sequential). The API Gateway 30s
  // limit means a batch of chapters in a single request times out (503); one call per
  // chapter keeps every request well under the ceiling and lets the UI fill in as it goes.
  // Pass specific chapter numbers to generate a chosen subset (some chapters get no image).
  const generateIllustrations = async (chapterNums?: number[]) => {
    if (!parsedBook) return;
    const nums = (chapterNums && chapterNums.length)
      ? chapterNums
      : parsedBook.chapters.map(ch => ch.number);
    const single = chapterNums?.length === 1;
    if (single) setRegeneratingChapter(nums[0]!); else setGeneratingIllustrations(true);
    addLog(`Generating IKON illustration${nums.length > 1 ? `s for ${nums.length} chapters, one at a time` : ` for chapter ${nums[0]}`}…`);

    let ok = 0;
    try {
      for (const n of nums) {
        setRegeneratingChapter(n);
        try {
          const res = await fetch(`${BASE_URL}/api/admin/books/illustrations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              language_code: parsedBook.metadata.languageCode,
              level: parsedBook.metadata.level,
              chapters: [{ number: n }],
            }),
          });
          if (!res.ok) { addLog(`Chapter ${n}: HTTP ${res.status}`, 'error'); continue; }
          const data = await res.json() as { illustrations: Array<{ chapter: number; url: string | null; error?: string }> };
          const ill = data.illustrations?.[0];
          if (ill?.url) {
            setIllustrations(prev => {
              const others = prev.filter(x => x.chapter !== n);
              return [...others, { chapter: n, url: ill.url, approved: false }].sort((a, b) => a.chapter - b.chapter);
            });
            ok++;
            addLog(`Chapter ${n}: illustration ready`);
          } else {
            addLog(`Chapter ${n}: ${ill?.error || 'IKON returned no image'}`, 'warn');
          }
        } catch (err) {
          addLog(`Chapter ${n}: ${err instanceof Error ? err.message : String(err)}`, 'error');
        }
      }
      if (nums.length > 1) addLog(`Done — ${ok}/${nums.length} illustrations generated`);
    } finally {
      setGeneratingIllustrations(false);
      setRegeneratingChapter(null);
    }
  };

  const uploadImage = async (file: File, slot: string) => {
    // Works BEFORE a book is generated too — fall back to the selected language/level so a
    // cover can be chosen for an as-yet-ungenerated level (e.g. A2 pre-order).
    const uLang = parsedBook?.metadata.languageCode || selectedLang;
    const uLevel = parsedBook?.metadata.level || selectedLevel;
    if (!uLang || !uLevel) { addLog('Pick a target language and level first.', 'error'); return; }
    setUploadingImage(true);
    addLog(`Uploading image for ${slot}…`);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
      const base64 = btoa(binary);

      const res = await fetch(`${BASE_URL}/api/admin/books/upload-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          language_code: uLang,
          level: uLevel,
          image_base64: base64,
          content_type: file.type || 'image/jpeg',
          slot,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { url: string; slot: string };

      if (slot === 'cover_male' || slot === 'cover_female') {
        const gender = slot === 'cover_male' ? 'male' : 'female';
        setCoverUrls(prev => prev ? { ...prev, [gender]: data.url } : { [gender]: data.url });
        setCoverApproval(prev => ({ ...prev, [gender]: 'approved' as const }));
        addLog(`Uploaded cover (${gender}): ${data.url.slice(0, 80)}…`);
      } else if (slot.startsWith('chapter_')) {
        const chNum = parseInt(slot.replace('chapter_', ''), 10);
        setIllustrations(prev => {
          const existing = prev.find(i => i.chapter === chNum);
          if (existing) return prev.map(i => i.chapter === chNum ? { ...i, url: data.url, approved: true } : i);
          return [...prev, { chapter: chNum, url: data.url, approved: true }];
        });
        addLog(`Uploaded illustration for chapter ${chNum}: ${data.url.slice(0, 80)}…`);
      }
    } catch (err) {
      addLog(`Upload error: ${err instanceof Error ? err.message : String(err)}`, 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCoverFileChange = (e: React.ChangeEvent<HTMLInputElement>, gender: string) => {
    const file = e.target.files?.[0];
    if (file) uploadImage(file, `cover_${gender}`);
    e.target.value = '';
  };

  const openPicker = (slot: ImagePickerSlot) => {
    setPickerSlot(slot);
    setPickerOpen(true);
  };

  // Recover orphaned book image generations (from the old cover/illustration buttons) into
  // the repository so they show up under Browse (tick "Book images only" to see just them).
  const recoverImages = async () => {
    // Scan the WHOLE language (not the current level — a new level like A2 has no images of its
    // own, which made this return a confusing 0). Then OPEN the picker so the images are right
    // there to browse and pick — that is what people expect from this button.
    const rLang = parsedBook?.metadata.languageCode || selectedLang;
    if (!rLang) { addLog('Pick a target language first.', 'error'); return; }
    setRecovering(true);
    try {
      const res = await fetch(`${BASE_URL}/api/admin/books/recover-images`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language_code: rLang }),
      });
      const d = await res.json();
      if (res.ok) {
        addLog(`Loaded ${d.recovered} image(s) from your ${langName(rLang)} library — opening the picker…`);
        setPickerSlot('cover_female' as ImagePickerSlot);
        setPickerOpen(true);
      } else {
        addLog(`Recover failed: ${d.error || res.status}`, 'error');
      }
    } catch (err) {
      addLog(`Recover error: ${err instanceof Error ? err.message : String(err)}`, 'error');
    } finally { setRecovering(false); }
  };

  const handlePickerSelect = (blob: Blob, slot: ImagePickerSlot) => {
    setPickerOpen(false);
    const file = new File([blob], `${slot}.jpg`, { type: 'image/jpeg' });
    uploadImage(file, slot);
  };

  return (
    <div className="space-y-6">
      <div className="bg-zinc-800 rounded-lg p-4 md:p-6">
        <h3 className="text-zinc-200 font-medium mb-4">Generate Book</h3>
        <p className="text-zinc-400 text-sm mb-4">
          Pull a CEFR document and generate EPUB + PDF. One book at a time.
        </p>

        <div className="flex flex-wrap gap-3">
          <div className="w-full sm:w-36">
            <label className="text-zinc-500 text-xs block mb-1">Base language</label>
            <select
              value={selectedBase}
              onChange={e => { setSelectedBase(e.target.value); setSelectedLang(''); setPreviewHtml(null); setParsedBook(null); setCopywriting(null); setEpubReady(false); setLogs([]); }}
              className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200"
            >
              {baseList.map(b => (
                <option key={b} value={b}>{BASE_LANG_NAMES[b] ?? b} ({b})</option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-64">
            <label className="text-zinc-500 text-xs block mb-1">Target language</label>
            <select
              value={selectedLang}
              onChange={e => { setSelectedLang(e.target.value); setPreviewHtml(null); setParsedBook(null); setCopywriting(null); setEpubReady(false); setLogs([]); }}
              className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200"
            >
              <option value="">Select language…</option>
              {langList.map(l => (
                <option key={l} value={l}>{langName(l)} ({l})</option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-48">
            <label className="text-zinc-500 text-xs block mb-1">Level</label>
            <select
              value={selectedLevel}
              onChange={e => { setSelectedLevel(e.target.value); setPreviewHtml(null); setParsedBook(null); setCopywriting(null); setEpubReady(false); setLogs([]); }}
              className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200"
            >
              {BOOK_LEVELS.map(lvl => (
                <option key={lvl} value={lvl}>{lvl} — {LEVEL_NAMES[lvl]} (${LEVEL_PRICES[lvl]})</option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={generate}
              disabled={!selectedLang || generating}
              className="px-4 py-2 rounded bg-teal-800 text-teal-200 text-sm font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >
              {generating ? 'Generating…' : 'Generate'}
            </button>
            <button
              onClick={() => openPicker('cover_female' as ImagePickerSlot)}
              disabled={!selectedLang || !selectedLevel}
              title="Open your image library to pick a cover — works before generating (e.g. A2 pre-order)"
              className="px-4 py-2 rounded bg-violet-700 text-white text-sm font-semibold hover:bg-violet-600 disabled:opacity-50 transition-colors ring-1 ring-violet-400"
            >
              🖼 Select cover image
            </button>
            {epubReady && (
              <>
                <button
                  onClick={downloadEpub}
                  className="px-4 py-2 rounded bg-indigo-800 text-indigo-200 text-sm font-medium hover:bg-indigo-700 transition-colors"
                >
                  Download EPUB
                </button>
                <button
                  onClick={downloadPdf}
                  className="px-4 py-2 rounded bg-amber-800 text-amber-200 text-sm font-medium hover:bg-amber-700 transition-colors"
                >
                  Print PDF
                </button>
                <button
                  onClick={saveToDatabase}
                  disabled={saving || !copywriting || hasUnapprovedImages()}
                  className="px-4 py-2 rounded bg-emerald-800 text-emerald-200 text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                  title={hasUnapprovedImages() ? 'Approve all IKON images first' : ''}
                >
                  {saving ? 'Saving…' : hasUnapprovedImages() ? 'Approve images first' : 'Save to DB'}
                </button>
                {/* Cover + chapter images are made per-slot below: pick a chapter to Browse
                    previously generated images (this book only) or generate one from a prompt
                    in Image Studio. No more bulk themed generation. */}
                <label className={`px-4 py-2 rounded bg-cyan-800 text-cyan-200 text-sm font-medium hover:bg-cyan-700 transition-colors cursor-pointer ${uploadingImage ? 'opacity-50 pointer-events-none' : ''}`}>
                  {uploadingImage ? 'Uploading…' : 'Upload Cover'}
                  <input type="file" accept="image/*" className="hidden" onChange={e => handleCoverFileChange(e, 'female')} />
                </label>
              </>
            )}
          </div>
        </div>

        {/* Pricing summary */}
        {selectedLang && (
          <div className="mt-4 p-3 bg-zinc-900/50 rounded text-xs text-zinc-400">
            <span className="text-zinc-300 font-medium">{langName(selectedLang)}</span>
            {' '}{selectedLevel} {LEVEL_NAMES[selectedLevel]} — Retail price: <span className="text-teal-400 font-medium">${LEVEL_PRICES[selectedLevel]}</span>
            {' '}· Bundle (A0–C2): <span className="text-teal-400 font-medium">$20</span>
          </div>
        )}
      </div>

      {/* Generation logs */}
      {logs.length > 0 && (
        <div className="bg-zinc-800 rounded-lg p-4 md:p-6">
          <h4 className="text-zinc-300 text-sm font-medium mb-3">Generation Log</h4>
          <div className="bg-zinc-900 rounded p-3 max-h-64 overflow-y-auto font-mono text-xs space-y-1">
            {logs.map((log, i) => (
              <div key={i} className={
                log.level === 'error' ? 'text-red-400' :
                log.level === 'warn' ? 'text-amber-400' :
                'text-zinc-400'
              }>
                <span className="text-zinc-600">{new Date(log.timestamp).toLocaleTimeString()}</span>
                {' '}{log.message}
              </div>
            ))}
            <div ref={logEndRef} />
          </div>
        </div>
      )}

      {/* IKON Cover Images — Approve / Regenerate */}
      {coverUrls && (coverUrls.male || coverUrls.female) && (
        <div className="bg-zinc-800 rounded-lg p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <h4 className="text-zinc-300 text-sm font-medium">IKON Cover Images</h4>
            <div className="text-xs text-zinc-500">
              {coverApproval.male === 'approved' && coverApproval.female === 'approved'
                ? <span className="text-emerald-400">Both approved</span>
                : 'Review each image below'}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(['male', 'female'] as const).map(gender => {
              const url = coverUrls[gender];
              if (!url) return null;
              const status = coverApproval[gender];
              return (
                <div key={gender} className={`rounded-lg border-2 p-3 transition-colors ${
                  status === 'approved' ? 'border-emerald-600 bg-emerald-900/10' :
                  status === 'rejected' ? 'border-red-600 bg-red-900/10' :
                  'border-zinc-700 bg-zinc-900/30'
                }`}>
                  <img src={url} alt={`${gender} cover character`} className="rounded max-h-56 mx-auto mb-3" />
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-xs capitalize">{gender} character</span>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => { setCoverApproval(prev => ({ ...prev, [gender]: 'approved' })); addLog(`Cover (${gender}): approved`); }}
                        disabled={status === 'approved'}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          status === 'approved'
                            ? 'bg-emerald-800 text-emerald-300'
                            : 'bg-zinc-700 text-zinc-300 hover:bg-emerald-800 hover:text-emerald-300'
                        }`}
                      >
                        {status === 'approved' ? 'Approved' : 'Approve'}
                      </button>
                      <button
                        onClick={() => { setCoverApproval(prev => ({ ...prev, [gender]: 'rejected' })); generateCover(gender); }}
                        disabled={regeneratingCover === gender}
                        className="px-3 py-1 rounded text-xs font-medium bg-zinc-700 text-zinc-300 hover:bg-amber-800 hover:text-amber-300 disabled:opacity-50 transition-colors"
                      >
                        {regeneratingCover === gender ? 'Regenerating…' : 'Regenerate'}
                      </button>
                      <label className={`px-3 py-1 rounded text-xs font-medium bg-cyan-800 text-cyan-300 hover:bg-cyan-700 cursor-pointer transition-colors ${uploadingImage ? 'opacity-50 pointer-events-none' : ''}`}>
                        Upload
                        <input type="file" accept="image/*" className="hidden" onChange={e => handleCoverFileChange(e, gender)} />
                      </label>
                      <button
                        onClick={() => openPicker(`cover_${gender}` as ImagePickerSlot)}
                        className="px-3 py-1 rounded text-xs font-medium bg-violet-800 text-violet-300 hover:bg-violet-700 transition-colors"
                      >
                        Browse
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-chapter images — click a chapter to browse previously generated images for THIS
          book (language-filtered) and pick one; or generate a new one from a prompt in Image
          Studio below. ● = a chapter already has an image assigned. */}
      {parsedBook && parsedBook.chapters.length > 0 && (
        <div className="bg-zinc-800 rounded-lg p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <h4 className="text-zinc-300 text-sm font-medium">Chapter images</h4>
            <div className="flex items-center gap-3">
              <button onClick={recoverImages} disabled={recovering}
                title="Pull previously generated book images into Browse (tick 'Book images only' there to see just them)"
                className="text-xs px-2.5 py-1 rounded bg-zinc-700 text-zinc-200 hover:bg-zinc-600 disabled:opacity-50">
                {recovering ? 'Recovering…' : 'Recover previous generations'}
              </button>
              <span className="text-xs text-zinc-500">Click a chapter to browse &amp; assign</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => openPicker('cover_female' as ImagePickerSlot)}
              title="Cover"
              className={`px-2 py-1 rounded text-[11px] border transition-colors ${coverUrls?.female || coverUrls?.male ? 'bg-emerald-900/30 border-emerald-700 text-emerald-200' : 'bg-zinc-900/40 border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}
            >
              Cover{(coverUrls?.female || coverUrls?.male) ? ' ●' : ''}
            </button>
            {parsedBook.chapters.map(ch => {
              const has = illustrations.some(i => i.chapter === ch.number && i.url);
              return (
                <button
                  key={ch.number}
                  onClick={() => openPicker(`chapter_${ch.number}` as ImagePickerSlot)}
                  title={`Chapter ${ch.number}: ${ch.title}`}
                  className={`px-2.5 py-1 rounded text-[11px] border transition-colors text-left max-w-[15rem] truncate ${has ? 'bg-emerald-900/30 border-emerald-700 text-emerald-200' : 'bg-zinc-900/40 border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}
                >
                  <span className="font-mono text-zinc-500">{ch.number}.</span> {ch.title}{has ? ' ●' : ''}
                </button>
              );
            })}
          </div>
          <p className="text-zinc-500 text-[11px] mt-2">● = image assigned. Click any chapter to open Browse (previously generated images for this book only). To create a new one, use Image Studio below, pick the chapter slot, write your prompt, then select the result. Click <b>Save to DB</b> when done — saved images load automatically next time.</p>
        </div>
      )}

      {/* IKON Chapter Illustrations — Approve / Regenerate */}
      {illustrations.length > 0 && (
        <div className="bg-zinc-800 rounded-lg p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <h4 className="text-zinc-300 text-sm font-medium">
              Chapter Illustrations ({illustrations.filter(i => i.approved).length}/{illustrations.filter(i => i.url).length} approved)
            </h4>
            {illustrations.every(i => !i.url || i.approved) && illustrations.some(i => i.url) && (
              <span className="text-emerald-400 text-xs">All approved</span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {illustrations.map(ill => (
              <div key={ill.chapter} className={`rounded-lg border-2 p-2 transition-colors ${
                ill.approved ? 'border-emerald-600 bg-emerald-900/10' : 'border-zinc-700 bg-zinc-900/30'
              }`}>
                {ill.url ? (
                  <img src={ill.url} alt={`Chapter ${ill.chapter}`} className="rounded max-h-28 mx-auto mb-2" />
                ) : (
                  <div className="h-28 bg-zinc-900/50 rounded flex items-center justify-center text-zinc-600 text-xs mb-2">No image</div>
                )}
                <p className="text-zinc-500 text-[10px] text-center mb-1.5">Ch {ill.chapter}</p>
                {ill.url && (
                  <div className="flex gap-1 justify-center">
                    <button
                      onClick={() => {
                        setIllustrations(prev => prev.map(i => i.chapter === ill.chapter ? { ...i, approved: true } : i));
                        addLog(`Chapter ${ill.chapter} illustration: approved`);
                      }}
                      disabled={ill.approved}
                      className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                        ill.approved
                          ? 'bg-emerald-800 text-emerald-300'
                          : 'bg-zinc-700 text-zinc-300 hover:bg-emerald-800 hover:text-emerald-300'
                      }`}
                    >
                      {ill.approved ? 'OK' : 'Approve'}
                    </button>
                    <button
                      onClick={() => generateIllustrations([ill.chapter])}
                      disabled={regeneratingChapter === ill.chapter}
                      className="px-2 py-0.5 rounded text-[10px] font-medium bg-zinc-700 text-zinc-300 hover:bg-amber-800 hover:text-amber-300 disabled:opacity-50 transition-colors"
                    >
                      {regeneratingChapter === ill.chapter ? '…' : 'Redo'}
                    </button>
                    <label className={`px-2 py-0.5 rounded text-[10px] font-medium bg-cyan-800 text-cyan-300 hover:bg-cyan-700 cursor-pointer transition-colors ${uploadingImage ? 'opacity-50 pointer-events-none' : ''}`}>
                      Upload
                      <input type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage(f, `chapter_${ill.chapter}`); e.target.value = ''; }} />
                    </label>
                    <button
                      onClick={() => openPicker(`chapter_${ill.chapter}` as ImagePickerSlot)}
                      className="px-2 py-0.5 rounded text-[10px] font-medium bg-violet-800 text-violet-300 hover:bg-violet-700 transition-colors"
                    >
                      Browse
                    </button>
                    <button
                      onClick={() => { setIllustrations(prev => prev.filter(i => i.chapter !== ill.chapter)); addLog(`Chapter ${ill.chapter}: image removed (Save to persist)`); }}
                      title="Remove this chapter's image — save to persist"
                      className="px-2 py-0.5 rounded text-[10px] font-medium bg-zinc-700 text-zinc-400 hover:bg-red-900 hover:text-red-300 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                )}
                {!ill.url && (
                  <div className="flex gap-1 justify-center">
                    <label className={`px-2 py-0.5 rounded text-[10px] font-medium bg-cyan-800 text-cyan-300 hover:bg-cyan-700 cursor-pointer transition-colors ${uploadingImage ? 'opacity-50 pointer-events-none' : ''}`}>
                      Upload
                      <input type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage(f, `chapter_${ill.chapter}`); e.target.value = ''; }} />
                    </label>
                    <button
                      onClick={() => openPicker(`chapter_${ill.chapter}` as ImagePickerSlot)}
                      className="px-2 py-0.5 rounded text-[10px] font-medium bg-violet-800 text-violet-300 hover:bg-violet-700 transition-colors"
                    >
                      Browse
                    </button>
                    <button
                      onClick={() => { setIllustrations(prev => prev.filter(i => i.chapter !== ill.chapter)); addLog(`Chapter ${ill.chapter}: removed from list`); }}
                      title="Remove this chapter row"
                      className="px-2 py-0.5 rounded text-[10px] font-medium bg-zinc-700 text-zinc-400 hover:bg-red-900 hover:text-red-300 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Copywriting */}
      {copywriting && (
        <div className="bg-zinc-800 rounded-lg p-4 md:p-6">
          <h4 className="text-zinc-300 text-sm font-medium mb-4">Landing Page Copywriting</h4>

          <div className="space-y-4">
            {/* Blurb */}
            <div>
              <div className="text-zinc-500 text-xs mb-1">Blurb</div>
              <p className="text-zinc-200 text-sm">{copywriting.blurb}</p>
            </div>

            {/* Description */}
            <div>
              <div className="text-zinc-500 text-xs mb-1">Description</div>
              <div className="text-zinc-300 text-sm whitespace-pre-line">{copywriting.description}</div>
            </div>

            {/* Features */}
            <div>
              <div className="text-zinc-500 text-xs mb-1">Features</div>
              <ul className="list-disc list-inside text-zinc-300 text-sm space-y-0.5">
                {copywriting.features.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>

            {/* What you'll learn */}
            <div>
              <div className="text-zinc-500 text-xs mb-1">What You'll Learn</div>
              <ul className="list-disc list-inside text-zinc-300 text-sm space-y-0.5">
                {copywriting.what_you_will_learn.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>

            {/* Stats grid */}
            <div>
              <div className="text-zinc-500 text-xs mb-2">Stats</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  ['Chapters', copywriting.stats.chapter_count],
                  ['Subsections', copywriting.stats.subsection_count],
                  ['Vocab Domains', copywriting.stats.vocab_domains],
                  ['Vocab Items', copywriting.stats.vocab_items],
                  ['Grammar Topics', copywriting.stats.grammar_topics],
                  ['Mini-dialogs', copywriting.stats.mini_dialogs],
                  ['Sentences', copywriting.stats.sample_sentences],
                  ['Words', copywriting.stats.word_count.toLocaleString()],
                ].map(([label, val]) => (
                  <div key={label as string} className="bg-zinc-900/50 rounded p-2 text-center">
                    <div className="text-teal-400 text-lg font-medium">{val}</div>
                    <div className="text-zinc-500 text-[10px]">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Target audience + Prerequisites */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-zinc-500 text-xs mb-1">Target Audience</div>
                <p className="text-zinc-300 text-sm">{copywriting.target_audience}</p>
              </div>
              <div>
                <div className="text-zinc-500 text-xs mb-1">Prerequisites</div>
                <p className="text-zinc-300 text-sm">{copywriting.prerequisites}</p>
              </div>
            </div>

            {/* Chapter summaries */}
            <div>
              <div className="text-zinc-500 text-xs mb-2">Chapter Summaries</div>
              <div className="space-y-1">
                {copywriting.chapter_summaries.map(ch => (
                  <div key={ch.number} className="flex gap-3 p-2 bg-zinc-900/30 rounded text-sm">
                    <span className="text-teal-400 font-mono text-xs shrink-0">Ch {ch.number}</span>
                    <div>
                      <span className="text-zinc-200">{ch.title}</span>
                      <span className="text-zinc-500 ml-2">{ch.summary}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* SEO */}
            <div className="border-t border-zinc-700 pt-3">
              <div className="text-zinc-500 text-xs mb-2">SEO</div>
              <div className="space-y-1 text-sm">
                <div><span className="text-zinc-500">Title:</span> <span className="text-zinc-300">{copywriting.seo_title}</span></div>
                <div><span className="text-zinc-500">Description:</span> <span className="text-zinc-300">{copywriting.seo_description}</span></div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {copywriting.keywords.map((kw, i) => (
                    <span key={i} className="px-2 py-0.5 bg-zinc-900 rounded text-[10px] text-zinc-400">{kw}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Studio */}
      {parsedBook && (
        <ImageStudio
          product="books"
          productKey={`${parsedBook.metadata.languageCode}_${parsedBook.metadata.level}`}
          languageCode={selectedLang}
          slots={[
            { value: 'cover', label: 'Cover' },
            ...(parsedBook.chapters || []).map(ch => ({ value: `chapter_${ch.number}`, label: `Ch ${ch.number}` })),
          ]}
          onSelect={(url, slot) => {
            // Image Studio's cover slot is the bare 'cover' (not 'cover_male'/'cover_female'), so the
            // old `startsWith('cover_')` check silently dropped every cover pick → cover_url never saved.
            if (slot === 'cover' || slot.startsWith('cover_')) {
              const gender = slot.includes('male') && !slot.includes('female') ? 'male' : 'female';
              setCoverUrls(prev => prev ? { ...prev, [gender]: url } : { [gender]: url });
              setCoverApproval(prev => ({ ...prev, [gender]: 'approved' as const }));
              addLog(`Image Studio: set ${slot} → ${url.slice(0, 60)}…`);
            } else if (slot.startsWith('chapter_')) {
              const chNum = parseInt(slot.replace(/^chapter_\d+_/, '').replace('chapter_', ''), 10) || parseInt(slot.match(/chapter_(\d+)/)?.[1] || '0', 10);
              setIllustrations(prev => {
                const existing = prev.find(i => i.chapter === chNum);
                if (existing) return prev.map(i => i.chapter === chNum ? { ...i, url, approved: true } : i);
                return [...prev, { chapter: chNum, url, approved: true }];
              });
              addLog(`Image Studio: set chapter ${chNum} illustration → ${url.slice(0, 60)}…`);
            }
          }}
        />
      )}

      {/* Book preview */}
      {previewHtml && (
        <div className="bg-zinc-800 rounded-lg p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <h4 className="text-zinc-300 text-sm font-medium">Book Preview</h4>
            {parsedBook && (
              <div className="text-xs text-zinc-500">
                {parsedBook.chapters.length} chapters ·{' '}
                {parsedBook.chapters.reduce((s, c) => s + c.subsections.length, 0)} subsections
              </div>
            )}
          </div>
          <div className="rounded-lg overflow-hidden border border-zinc-700">
            <iframe
              srcDoc={previewHtml}
              title="Book preview"
              className="w-full bg-white"
              style={{ height: '80vh' }}
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      )}

      {/* Image Picker from video repository */}
      <ImagePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickerSelect}
        slot={pickerSlot}
        languageCode={selectedLang || undefined}
      />
    </div>
  );
}

// ── Server-render pipeline: generate → QC (HTML/PDF/EPUB) → publish/update/cancel ──
function Pipeline() {
  const [line, setLine] = useState('cefr');
  // Start blank so opening the tab loads NOTHING — the operator picks a language + level first, and
  // only then does the chosen book load. (Previously defaulted to CMN_TW/A1 and auto-fetched that book
  // on open, forcing a wait for a book nobody asked for.)
  const [lang, setLang] = useState('');
  const [level, setLevel] = useState('');
  const [draft, setDraft] = useState<any>(null);
  const reqRef = useRef(0);
  const [busy, setBusy] = useState('');
  const [log, setLog] = useState<string[]>([]);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [docs, setDocs] = useState<CefrDocEntry[]>([]);
  const addLog = (m: string) => setLog(l => [...l.slice(-14), m]);

  // Both the CEFR and companion dropdowns list EVERY language in development (the CEFR-doc set), not a
  // hardcoded few — the menu is tied to what is being developed, so new languages appear automatically.
  useEffect(() => { fetchDocList().then(setDocs); }, []);
  const cefrLangs = [...new Set(docs.map(d => d.language_code))].filter(c => c && !/^ERRATA/i.test(c)).sort((a, b) => langName(a).localeCompare(langName(b)));
  const langOptions = cefrLangs;
  useEffect(() => {
    // Only reset the language if one is already chosen but invalid for the new book type — never
    // auto-fill from the blank start state; the operator makes the first pick.
    if (lang && langOptions.length && !langOptions.includes(lang)) setLang(langOptions[0]!);
    /* eslint-disable-next-line */
  }, [line, docs]);

  const q = `line=${line}&language=${lang}&level=${level}`;
  const refresh = async () => {
    const id = ++reqRef.current;              // tag this as the latest request
    if (!lang || !level) { setDraft(null); return null; }
    try {
      const r = await fetch(`${BASE_URL}/api/admin/books/draft?${q}`);
      if (id !== reqRef.current) return null;  // selection changed meanwhile — ignore stale result
      if (r.ok) { const d = await r.json(); setDraft(d); return d; }
    } catch { /* ignore */ }
    if (id === reqRef.current) setDraft(null);
    return null;
  };
  // On product change: reset and load THIS product's own saved cover — the exact image that Publish
  // will send for this book type. It comes from the product's pipeline record (draft.cover_image_url),
  // so a Companion/Audio book shows its OWN cover, never the CEFR book's photo (the old code read the
  // shared book#<level> record for every type, which is why audio books showed the wrong image).
  useEffect(() => {
    setDraft(null); setCoverUrl(null);
    if (!lang || !level) return;   // nothing loads until a language AND level are both picked
    let cancelled = false;
    (async () => {
      const d = await refresh();
      if (!cancelled && d && d.cover_image_url) setCoverUrl(d.cover_image_url);
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [line, lang, level]);

  const post = (path: string) => fetch(`${BASE_URL}/api/admin/books/${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ line, language: lang, level }),
  });

  const generate = async () => {
    setBusy('generate'); addLog('Rendering server-side — HTML + PDF + EPUB…');
    await post('generate');
    for (let i = 0; i < 40; i++) {
      await new Promise(r => setTimeout(r, 4000));
      const d = await refresh();
      if (d && d.status === 'draft') { addLog(`Draft ready — ${d.qa_count ?? ''} Q&A.`); break; }
      if (i === 39) addLog('Still rendering — check back shortly.');
    }
    setBusy('');
  };
  const act = async (path: string, label: string) => {
    setBusy(path); addLog(`${label}…`);
    try { const d = await (await post(path)).json(); addLog(d.status === 'published' ? `Published (masters: ${Object.keys(d.master_keys || {}).join(', ')}).` : d.status === 'canceled' ? 'Draft discarded.' : JSON.stringify(d).slice(0, 120)); }
    catch (e) { addLog('Error: ' + (e as Error).message); }
    await refresh(); setBusy('');
  };
  // Render the book's front cover as a store-listing image. The backend saves it on the pipeline
  // record, so it's sent to Blitzscale on the next Publish / Update. Pass photoUrl to frame a photo
  // you picked for THIS product; omit it to reuse the shared per-language/level illustration.
  const genCover = async (photoUrl?: string) => {
    setBusy('cover'); addLog(photoUrl ? 'Framing your chosen photo as the cover…' : 'Rendering cover image…');
    try {
      const r = await fetch(`${BASE_URL}/api/admin/books/sample-images`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, language: lang, level, cover: true, ...(photoUrl ? { coverImageUrl: photoUrl } : {}) }),
      });
      const d = await r.json();
      if (d.cover_url) { setCoverUrl(d.cover_url); addLog('Cover image ready — it will be sent to the store on Publish / Update.'); }
      else addLog('Cover render failed: ' + (d.error || JSON.stringify(d).slice(0, 100)));
    } catch (e) { addLog('Cover error: ' + (e as Error).message); }
    setBusy('');
  };

  // Pick a photo for THIS product's cover from the image library. The cropped photo is uploaded with
  // skip_book_record:true so it never touches the shared `book#<level>` illustration (the CEFR book's
  // photo), then framed into this line's cover and saved on this product's pipeline record.
  const choosePhoto = async (blob: Blob) => {
    setPickerOpen(false);
    setBusy('cover'); addLog('Uploading your chosen photo…');
    try {
      const bytes = new Uint8Array(await blob.arrayBuffer());
      let binary = ''; for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
      const base64 = btoa(binary);
      const r = await fetch(`${BASE_URL}/api/admin/books/upload-image`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language_code: lang, level, image_base64: base64, content_type: 'image/jpeg', slot: `${line}_cover`, skip_book_record: true }),
      });
      const d = await r.json();
      if (!r.ok || !d.url) { addLog('Photo upload failed: ' + (d.error || r.status)); setBusy(''); return; }
      await genCover(d.url);
    } catch (e) { addLog('Photo error: ' + (e as Error).message); setBusy(''); }
  };

  // Remove the store cover from THIS book. Clears it on the product record so it STICKS — publish is
  // then blocked until a new cover is chosen, instead of silently re-seeding the CEFR book's image.
  const removeCover = async () => {
    setBusy('cover'); addLog('Removing the store cover from this book…');
    try {
      const r = await fetch(`${BASE_URL}/api/admin/books/clear-cover`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, language: lang, level }),
      });
      if (r.ok) { setCoverUrl(null); addLog('Cover removed. This book has no store cover now — pick a new one before publishing.'); }
      else addLog('Could not remove the cover: ' + (await r.text()).slice(0, 120));
    } catch (e) { addLog('Remove error: ' + (e as Error).message); }
    setBusy('');
  };

  // Use an image CREATED right here (Image Studio) as this product's cover — no detour to the Generate
  // tab. For a CEFR book the image is also saved on the shared book#<level> record so the printed cover
  // page matches the store cover; for Companion/Audio it stays this product's own image. Then frame it.
  const useCreatedImage = async (url: string) => {
    if (line === 'cefr') {
      try {
        await fetch(`${BASE_URL}/api/admin/books/${encodeURIComponent(`${lang}_${level}`)}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cover_url: url, status: 'ready' }),
        });
      } catch { /* non-fatal — framing still proceeds */ }
    }
    await genCover(url);
  };

  const st = draft?.status || 'none';
  const stColor = st === 'published' ? 'bg-teal-900 text-teal-300' : st === 'draft' ? 'bg-amber-900 text-amber-300' : st === 'generating' ? 'bg-blue-900 text-blue-300' : 'bg-zinc-800 text-zinc-400';
  const sel = 'w-full sm:w-40 rounded bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-zinc-100';
  const btn = 'rounded px-4 py-2 text-sm font-medium disabled:opacity-50';

  return (
    <div className="space-y-5">
      <p className="text-sm text-zinc-400">Render the book <b className="text-zinc-200">server-side</b> (no print window), review the HTML / PDF / EPUB, then publish or overwrite the store listing. Companion books read the audio-course corpus; CEFR uses the level document.</p>

      <div className="flex flex-wrap gap-3">
        <label className="block"><span className="text-xs text-zinc-400">Book type</span>
          <select className={sel} value={line} onChange={e => setLine(e.target.value)}>
            <option value="cefr">CEFR level book</option>
            <option value="companion">Companion (Q&amp;A)</option>
          </select></label>
        <label className="block"><span className="text-xs text-zinc-400">Language</span>
          <select className={sel} value={lang} onChange={e => setLang(e.target.value)}>
            <option value="">Language…</option>
            {langOptions.map(l => <option key={l} value={l}>{langName(l)} ({l})</option>)}
          </select></label>
        <label className="block"><span className="text-xs text-zinc-400">Level</span>
          <select className={sel} value={level} onChange={e => setLevel(e.target.value)}>
            <option value="">Level…</option>
            {['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(l => <option key={l} value={l}>{l}</option>)}
          </select></label>
        <div className="flex items-end gap-2">
          <button onClick={generate} disabled={!!busy || !lang || !level} className={`${btn} bg-teal-800 text-teal-100 hover:bg-teal-700`}>{busy === 'generate' ? 'Rendering…' : 'Generate'}</button>
          <button onClick={() => setPickerOpen(true)} disabled={!!busy || !lang || !level} title="Pick a photo for THIS book's cover from your image library — does not change any other book's photo" className={`${btn} bg-violet-700 text-white ring-1 ring-violet-400 hover:bg-violet-600`}>🖼 Select cover image</button>
          <button onClick={() => genCover()} disabled={!!busy || !lang || !level} title="Frame the current cover photo as the store-listing image (sent to Blitzscale on Publish)" className={`${btn} bg-violet-800 text-violet-100 hover:bg-violet-700`}>{busy === 'cover' ? 'Rendering cover…' : 'Generate cover image'}</button>
          <span className={`self-center px-2 py-1 rounded text-[11px] font-medium ${stColor}`}>{st}{draft?.draft_version ? ` v${draft.draft_version}` : ''}</span>
        </div>
      </div>

      {coverUrl && (
        <div className="flex items-center gap-3 bg-zinc-800/50 rounded-lg p-3">
          <img src={coverUrl} alt="store cover" className="h-32 rounded border border-zinc-700" />
          <div className="text-xs text-zinc-400 min-w-0">
            <div className="text-zinc-200 font-medium mb-1">Store cover image {line === 'cefr' ? '' : <span className="text-emerald-400">· this audio course’s own cover</span>}</div>
            Sent to the store on <b className="text-zinc-300">Publish</b>.
            <a href={coverUrl} target="_blank" rel="noreferrer" className="block text-teal-400 hover:underline break-all mt-1">{coverUrl}</a>
            <button onClick={removeCover} disabled={!!busy} className="mt-2 px-3 py-1 rounded text-xs font-medium bg-red-900/40 text-red-300 ring-1 ring-red-800 hover:bg-red-900/70 disabled:opacity-50">Remove cover</button>
          </div>
        </div>
      )}

      {/* Create a cover image right here — no need to switch to the Generate tab. The image you make or
          pick is framed onto THIS product's cover (a Companion/Audio book keeps its own image). */}
      {lang && level && (
        <div className="bg-zinc-800/40 rounded-lg p-3">
          <div className="text-xs text-zinc-400 mb-2">Create a cover image for this book — generate from a prompt below, or use <b className="text-zinc-300">Select cover image</b> above to pick an existing one. The result is framed onto this book automatically.</div>
          <ImageStudio
            product="books"
            productKey={`${lang}_${level}`}
            languageCode={lang}
            slots={[{ value: 'cover', label: 'Cover' }]}
            onSelect={(url) => useCreatedImage(url)}
          />
        </div>
      )}

      {((draft?.view && (draft.view.html || draft.view.pdf || draft.view.epub)) || (draft?.download && (draft.download.pdf || draft.download.epub))) && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            {draft?.download?.pdf && <a href={draft.download.pdf} className={`${btn} bg-teal-800 text-teal-100 hover:bg-teal-700`}>Download PDF</a>}
            {draft?.download?.epub && <a href={draft.download.epub} className={`${btn} bg-teal-800 text-teal-100 hover:bg-teal-700`}>Download EPUB</a>}
            {draft?.view?.pdf && <a href={draft.view.pdf} target="_blank" rel="noreferrer" className={`${btn} bg-zinc-800 text-zinc-200 hover:bg-zinc-700`}>Open PDF</a>}
            <div className="flex-1" />
            <button onClick={() => act('cancel', 'Discarding draft')} disabled={!!busy || st !== 'draft'} className={`${btn} bg-zinc-800 text-zinc-400 hover:text-zinc-200`}>Cancel</button>
            <button onClick={() => act('publish', 'Publishing')} disabled={!!busy || (st !== 'draft' && st !== 'published')} title="Publish this book to the store (creates the listing, or updates it if already live). Blocked unless every component — cover, PDF, EPUB, title, price — is present." className={`${btn} bg-teal-800 text-teal-100 hover:bg-teal-700`}>Publish</button>
          </div>
          {/* Inline QC viewer: HTML when present (companion), otherwise the PDF itself. */}
          {(draft.view.html || draft.view.pdf) &&
            <iframe title="QC preview" src={draft.view.html || draft.view.pdf} className="w-full h-[70vh] rounded border border-zinc-700 bg-white" />}
        </div>
      )}

      {log.length > 0 && (
        <pre className="text-[11px] text-zinc-500 bg-zinc-900 rounded p-3 overflow-x-auto whitespace-pre-wrap">{log.join('\n')}</pre>
      )}

      <ImagePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(blob) => choosePhoto(blob)}
        slot={'cover_female' as ImagePickerSlot}
        languageCode={lang}
      />
    </div>
  );
}

// ── Covers: read-only directory of every stored cover-image URL (for email ads). ────
// Purely reads `GET /api/admin/books/covers` — never renders, generates, or overwrites a book.
type CoverRow = {
  language_code: string; level: string; level_name: string;
  illustration_url: string | null;
  cefr_cover_url: string | null; companion_cover_url: string | null;
  cefr_status: string | null; companion_status: string | null;
  cefr_store_url: string | null; companion_store_url: string | null;
  title: string | null; updated_at: string | null;
};

type AudioCourseCover = {
  lang: string; level: string;
  cover_image_url: string | null;      // framed product cover (the whole cover)
  illustration_url: string | null;     // raw cover image / photo (for email ads)
  title: string | null;
  bonus_covers: string[];
  store_url: string | null;
  listing_id: string | null;
  updated_at: string | null;
};

function CoverDirectory() {
  const [rows, setRows] = useState<CoverRow[]>([]);
  const [acRows, setAcRows] = useState<AudioCourseCover[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    try {
      // cache-bust: always pull the live cover directory (it self-updates as products/covers change).
      const r = await fetch(`${BASE_URL}/api/admin/books/covers?t=${Date.now()}`, { cache: 'no-store' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRows(d.covers || []);
      setAcRows(d.audio_course_covers || []);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const copy = async (url: string) => {
    try { await navigator.clipboard.writeText(url); setCopied(url); setTimeout(() => setCopied(c => c === url ? null : c), 1500); }
    catch { /* clipboard blocked — the URL is still visible to select manually */ }
  };

  const f = filter.trim().toLowerCase();
  const visible = f
    ? rows.filter(r => langName(r.language_code).toLowerCase().includes(f) || r.language_code.toLowerCase().includes(f) || r.level.toLowerCase().includes(f))
    : rows;
  const acVisible = f
    ? acRows.filter(r => langName(r.lang).toLowerCase().includes(f) || r.lang.toLowerCase().includes(f) || r.level.toLowerCase().includes(f) || (r.title || '').toLowerCase().includes(f))
    : acRows;

  const Cell = ({ url, storeUrl, status }: { url: string | null; storeUrl?: string | null; status?: string | null }) => {
    if (!url) return <span className="text-zinc-600">—</span>;
    return (
      <div className="flex items-start gap-2 min-w-[220px]">
        <a href={url} target="_blank" rel="noreferrer" className="shrink-0">
          <img src={url} alt="cover" className="h-16 w-12 object-cover rounded border border-zinc-700" loading="lazy" />
        </a>
        <div className="min-w-0 text-xs">
          {status && <div className="mb-1"><span className={`px-1.5 py-0.5 rounded text-[10px] ${status === 'published' ? 'bg-teal-900 text-teal-300' : 'bg-amber-900 text-amber-300'}`}>{status}</span></div>}
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => copy(url)} className="px-2 py-0.5 rounded bg-zinc-700 text-zinc-200 hover:bg-zinc-600">{copied === url ? '✓ Copied' : 'Copy URL'}</button>
            <a href={url} target="_blank" rel="noreferrer" className="px-2 py-0.5 rounded bg-zinc-800 text-teal-400 hover:underline">Open</a>
            {storeUrl && <a href={storeUrl} target="_blank" rel="noreferrer" className="px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 hover:text-zinc-200">Store</a>}
          </div>
          <div className="text-zinc-500 break-all mt-1">{url}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-zinc-400">
          Every stored cover-image URL, read-only — <b className="text-zinc-300">no rendering or generating</b>, nothing is
          overwritten. <b className="text-zinc-300">Store cover</b> = the finished framed image sent to the store (use this for
          email ads); <b className="text-zinc-300">Illustration</b> = the raw chosen photo. The second table below lists the
          <b className="text-zinc-300"> audio-course product covers</b> (the store-card image for each course).
        </p>
        <div className="flex gap-2">
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter language / level…" className="w-full sm:w-56 rounded bg-zinc-900 border border-zinc-700 px-3 py-1.5 text-sm text-zinc-200" />
          <button onClick={load} disabled={loading} className="px-3 py-1.5 rounded bg-zinc-800 text-zinc-300 text-sm hover:text-zinc-100 disabled:opacity-50">{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      {error && <p className="text-sm text-red-400">Failed to load: {error}</p>}
      {!loading && !error && visible.length === 0 && acVisible.length === 0 && <p className="text-sm text-zinc-500">No covers yet.</p>}

      {visible.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-zinc-300">Book covers <span className="text-zinc-500 font-normal">(CEFR + companion books)</span></h3>
          <div className="overflow-x-auto rounded-lg border border-zinc-800">
            <table className="min-w-[760px] w-full text-sm">
              <thead className="bg-zinc-800/60 text-zinc-400 text-xs">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Book</th>
                  <th className="text-left px-3 py-2 font-medium">CEFR store cover</th>
                  <th className="text-left px-3 py-2 font-medium">Companion store cover</th>
                  <th className="text-left px-3 py-2 font-medium">Illustration (photo)</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(r => (
                  <tr key={`${r.language_code}_${r.level}`} className="border-t border-zinc-800 align-top">
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-zinc-200 font-medium">{langName(r.language_code)}</div>
                      <div className="text-zinc-500 text-xs">{r.language_code} · {r.level}{r.level_name ? ` — ${r.level_name}` : ''}</div>
                    </td>
                    <td className="px-3 py-3"><Cell url={r.cefr_cover_url} storeUrl={r.cefr_store_url} status={r.cefr_status} /></td>
                    <td className="px-3 py-3"><Cell url={r.companion_cover_url} storeUrl={r.companion_store_url} status={r.companion_status} /></td>
                    <td className="px-3 py-3"><Cell url={r.illustration_url} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {acVisible.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-zinc-300">Audio-course product covers <span className="text-zinc-500 font-normal">(store card — use for email ads)</span></h3>
          <div className="overflow-x-auto rounded-lg border border-zinc-800">
            <table className="min-w-[760px] w-full text-sm">
              <thead className="bg-zinc-800/60 text-zinc-400 text-xs">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Course</th>
                  <th className="text-left px-3 py-2 font-medium">Product cover <span className="text-zinc-500 font-normal">(framed)</span></th>
                  <th className="text-left px-3 py-2 font-medium">Cover image <span className="text-zinc-500 font-normal">(photo)</span></th>
                  <th className="text-left px-3 py-2 font-medium">Bonus covers</th>
                </tr>
              </thead>
              <tbody>
                {acVisible.map(r => (
                  <tr key={`${r.lang}_${r.level}`} className="border-t border-zinc-800 align-top">
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-zinc-200 font-medium">{langName(r.lang)}</div>
                      <div className="text-zinc-500 text-xs">{r.lang} · {r.level}</div>
                      {r.title && <div className="text-zinc-400 text-xs mt-1 max-w-[220px]">{r.title}</div>}
                    </td>
                    <td className="px-3 py-3"><Cell url={r.cover_image_url} storeUrl={r.store_url} /></td>
                    <td className="px-3 py-3"><Cell url={r.illustration_url} /></td>
                    <td className="px-3 py-3">
                      {r.bonus_covers.length === 0 ? <span className="text-zinc-600">—</span> : (
                        <div className="space-y-2">
                          {r.bonus_covers.map((u, i) => <Cell key={i} url={u} />)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────

export default function Books() {
  const [tab, setTab] = useState<Tab>('generate');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-zinc-100">Books</h1>
        <div className="flex gap-1 bg-zinc-800 rounded p-0.5">
          {([
            ['browse', 'Browse'],
            ['preflight', 'Pre-flight'],
            ['generate', 'Generate'],
            ['pipeline', 'Server render'],
            ['covers', 'Covers'],
          ] as [Tab, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-1.5 rounded text-xs font-medium transition-colors ${
                tab === key ? 'bg-teal-800 text-teal-200' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary banner */}
      <div className="bg-zinc-800/50 rounded-lg p-4 flex flex-wrap gap-4 items-center text-sm">
        <div className="text-zinc-400">
          <span className="text-zinc-200 font-medium">7 levels</span> per language ·{' '}
          <span className="text-zinc-200 font-medium">EPUB + PDF</span> ·{' '}
          CEFR A0–C2
        </div>
        <div className="flex flex-wrap gap-1">
          {BOOK_LEVELS.map(lvl => {
            const c = LEVEL_COLORS[lvl] ?? DEFAULT_COLOR;
            return (
              <span key={lvl} className={`px-2 py-0.5 rounded text-[10px] font-medium ${c.badge}`}>
                {lvl} ${LEVEL_PRICES[lvl]}
              </span>
            );
          })}
          <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-teal-900 text-teal-300">
            Bundle $20
          </span>
        </div>
      </div>

      {tab === 'browse' ? <BrowseBooks /> :
       tab === 'preflight' ? <Preflight /> :
       tab === 'pipeline' ? <Pipeline /> :
       tab === 'covers' ? <CoverDirectory /> :
       <GenerateBooks />}
    </div>
  );
}
